import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Collapse, IconButton, Paper } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import styled from '@emotion/styled';
import moment from 'moment';

import { ESSENTIAL_COOKIES } from 'app';
import { Link, Text } from 'components';
import { appConfigSelector } from 'reducers/app.reducer';
import { ROUTES } from '../constants';
import { ICustomTheme } from 'interfaces';

const StyledFixedContainer = styled(Paper)(
  ({ theme }: { theme: ICustomTheme }) => ({
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(5),
    width: '40%',
    padding: theme.spacing(1, 3, 3),
    //zIndex is used to hide the fixed component of navigation on mobile version
    zIndex: 10,
    backgroundColor: theme.colors.white,
    [theme.breakpoints.down('sm')]: {
      bottom: 0,
      right: 0,
      width: '100%',
    },
  }),
);

const StyledRelativeContainer = styled.div({
  position: 'relative',
});

const StyledIconButton = styled(IconButton)(
  ({ theme }: { theme: ICustomTheme }) => ({
    position: 'absolute',
    top: 0,
    right: 0,
    padding: theme.spacing(0.5),
    color: theme.colors.grey.light,
  }),
);

const StyledCloseIcon = styled(CloseIcon)({
  fontSize: 26,
});

const StyledTextContainer = styled.div(
  ({ theme }: { theme: ICustomTheme }) => ({
    paddingTop: theme.spacing(5),
    '& *': {
      fontSize: theme.typography.h5.fontSize,
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.h6.fontSize,
      },
    },
  }),
);

const StyledLink = styled(Link)(({ theme }: { theme: ICustomTheme }) => ({
  paddingRight: theme.spacing(0.5),
  color: theme.colors.paletteFromBO.main,
}));

const StyledText = styled(Text)(({ theme }: { theme: ICustomTheme }) => ({
  display: 'inline',
  paddingRight: theme.spacing(0.5),
}));

export const CookiesInfos = ({
  isEssentialCookieConsidered,
  setCookie,
}: {
  isEssentialCookieConsidered: boolean;
  setCookie: (
    key: string,
    value: string,
    options?: { path: string; expires: Date },
  ) => void;
}) => {
  const { t } = useTranslation();

  const {
    programUrl,
    legalMentionsPath,
    dataProtectionsPath,
    cookiesConditionsPath,
  } = useSelector(appConfigSelector);

  const handleClick = () => {
    const sixMonthsLaterDate = moment().add(6, 'months').toDate();
    // Set to 'accepted' to be in sync with the v1 cookie management value
    // This way both v1 and v2 use the same cookie to know if the cookie banner has been considered
    setCookie(ESSENTIAL_COOKIES, 'accepted', {
      path: ROUTES.HOME,
      expires: sixMonthsLaterDate,
    });
  };

  const cookiesTextSection = [
    <StyledText size="caption" isParagraph={false}>
      {t('cookies.intro')}
    </StyledText>,
    <StyledText size="caption" isParagraph={false}>
      {t('cookies.intermediateText1')}
    </StyledText>,
    <StyledLink
      color="primary"
      size="caption"
      weight="bold"
      url={`${programUrl}${cookiesConditionsPath}`}
      target="_blank"
      rel="noopener noreferrer"
      isParagraph={false}
    >
      {t('cookies.cookiesPolicy')}
    </StyledLink>,
    <StyledText size="caption" isParagraph={false}>
      {t('cookies.intermediateText2')}
    </StyledText>,
    <StyledLink
      color="primary"
      size="caption"
      weight="bold"
      url={`${programUrl}${legalMentionsPath}`}
      target="_blank"
      rel="noopener noreferrer"
      isParagraph={false}
    >
      {t('cookies.legalNotice')}
    </StyledLink>,
    <StyledText size="caption" isParagraph={false}>
      {t('cookies.intermediateText3')}
    </StyledText>,
    <StyledLink
      color="primary"
      size="caption"
      weight="bold"
      url={`${programUrl}${dataProtectionsPath}`}
      target="_blank"
      rel="noopener noreferrer"
      isParagraph={false}
    >
      {t('cookies.dataPolicy')}
    </StyledLink>,
  ];

  return (
    <Collapse in={!isEssentialCookieConsidered} timeout="auto" unmountOnExit>
      <StyledFixedContainer elevation={4}>
        <StyledRelativeContainer>
          <StyledIconButton aria-label="close dialog" onClick={handleClick}>
            <StyledCloseIcon />
          </StyledIconButton>
          <StyledTextContainer>{cookiesTextSection}</StyledTextContainer>
        </StyledRelativeContainer>
      </StyledFixedContainer>
    </Collapse>
  );
};
